/**
 * Component: EventOverviewTab
 * Overview information for an event: what it is, how registration is configured and
 * how people register / pay. Name, status and the Editar button live in the page header.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Banknote,
  Calendar,
  CalendarClock,
  FileSignature,
  FileText,
  Image as ImageIcon,
  MapPin,
  UsersRound,
} from 'lucide-react';
import type { Event, EventStatus } from '@/types/event.types';
import { formatDateRange, formatPrice, getEventDuration } from '@/models/event';
import { getFlyerUrl } from '@/data/eventRepository';
import { PublicRegistrationLinkCard } from './PublicRegistrationLinkCard';
import { PaymentInfoCard } from './PaymentInfoCard';
import { EventLifecycleStepper } from './EventLifecycleStepper';
import { useEventParticipantStatistics } from '../hooks/useEventParticipants';

/** One line with the numbers that matter; the clickable cards live in the Participantes tab */
function EventSummaryLine({ eventId }: { eventId: string }) {
  const { data: s } = useEventParticipantStatistics(eventId);
  if (!s) return null;
  const parts = [
    `${s.total} inscritos (${s.ministers} ministros, ${s.guests} invitados)`,
    s.pre_registered > 0 ? `${s.pre_registered} por revisar` : null,
    `${formatPrice(Number(s.collected))} recaudados`,
    Number(s.outstanding) > 0 ? `${formatPrice(Number(s.outstanding))} por cobrar` : null,
    `${s.attended}/${s.total} asistieron`,
  ].filter(Boolean);
  return (
    <p className="rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">{parts.join(' · ')}</p>
  );
}

function Detail({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="rounded-lg bg-primary/10 p-2">
        <Icon className="size-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

const FORM_TYPE_LABELS: Record<string, string> = {
  SIMPLE: 'Simple (datos básicos)',
  FULL_CONVENTION: 'Convención completa',
};
const SIGNATURE_LABELS: Record<string, string> = {
  ON_SITE: 'Presencial (se firma impreso)',
  DIGITAL: 'Digital (en el formulario)',
};

interface EventOverviewTabProps {
  event: Event;
  canTransition: boolean;
  onRequestStatus: (target: EventStatus) => void;
}

export function EventOverviewTab({ event, canTransition, onRequestStatus }: EventOverviewTabProps) {
  const flyerUrl = getFlyerUrl(event.flyer_filename);
  const deadline = event.registration_deadline ? new Date(event.registration_deadline) : null;
  const duration = getEventDuration(event);

  return (
    <div className="space-y-6">
      <EventLifecycleStepper status={event.status as EventStatus} />
      <EventSummaryLine eventId={event.id} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Detalles del evento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {event.description && <p className="text-sm text-muted-foreground">{event.description}</p>}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Detail icon={FileText} label="Código del evento">
                  {event.code}
                </Detail>
                <Detail icon={Calendar} label="Fechas">
                  <p>{formatDateRange(event)}</p>
                  <p className="text-xs">
                    {duration} {duration === 1 ? 'día' : 'días'}
                  </p>
                </Detail>
                <Detail icon={MapPin} label="Ubicación">
                  {event.location || 'No especificada'}
                </Detail>
                <Detail icon={Banknote} label="Precio base">
                  {formatPrice(event.base_price || 0)}
                </Detail>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configuración de inscripción</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Detail icon={CalendarClock} label="Plazo de inscripción">
                {deadline ? deadline.toLocaleString('es-BO') : 'Sin plazo'}
              </Detail>
              <Detail icon={FileText} label="Formulario">
                {FORM_TYPE_LABELS[event.registration_form_type ?? 'SIMPLE'] ?? event.registration_form_type}
              </Detail>
              <Detail icon={FileSignature} label="Firma">
                {SIGNATURE_LABELS[event.signature_mode ?? 'ON_SITE'] ?? event.signature_mode}
              </Detail>
              <Detail icon={UsersRound} label="Invitados por formulario público">
                {event.allow_public_guests ? 'Permitidos' : 'No permitidos'}
              </Detail>
            </CardContent>
          </Card>

          {event.agenda && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="size-5" />
                  Orden del día
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{event.agenda}</p>
              </CardContent>
            </Card>
          )}

          {flyerUrl && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="size-5" />
                  Afiche del evento
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/50 p-4">
                  <img
                    src={flyerUrl}
                    alt={`Afiche de ${event.name}`}
                    className="mx-auto h-auto max-h-96 max-w-full rounded"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <PublicRegistrationLinkCard event={event} canTransition={canTransition} onRequestStatus={onRequestStatus} />

          <PaymentInfoCard event={event} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Historial de estado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {[
                ['Creado', event.created_at],
                ['Publicado', event.published_at],
                ['Cerrado', event.closed_at],
                ['Completado', event.completed_at],
              ]
                .filter(([, value]) => !!value)
                .map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">{label}:</span>
                    <span>{new Date(value as string).toLocaleString('es-BO')}</span>
                  </div>
                ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
